"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import JsonLd from "@/components/JsonLd";
import { APP_LOGIN_URL } from "@/lib/site-config";

const FAQS: Array<{
  q: string;
  a: string;
  cta?: { label: string; href: string };
}> = [
  {
    q: "What is Instanodes?",
    a: "Instanodes is a web3 infra provider for protocol teams, developers, and enterprises. We provide managed RPC Endpoints, dedicated nodes, validator nodes, and appchain development. Everything deploys in under 5 minutes without DevOps overhead. Clients use Instanodes to power production DeFi protocols, institutional staking, NFT platforms, and Web3 gaming infrastructure across 50+ blockchain networks.",
  },
  {
    q: "How quickly can I deploy a blockchain node?",
    a: "Nodes go live in under 5 minutes. Select your chain, configure your endpoint in the deployment dashboard, and receive a live RPC URL. No hardware provisioning. No sync delays. No infrastructure management required. Archive node access is included on paid plans for full historical query support.",
    cta: { label: "Start for free", href: APP_LOGIN_URL },
  },
  {
    q: "Do you provide RPC Endpoints for Web3 applications?",
    a: "Yes. Instanodes provides managed RPC Endpoints across 50+ blockchain networks with sub-100ms response latency and a contractual 99.95% uptime SLA. Developers use our APIs to power DeFi protocols, wallet infrastructure, NFT marketplaces, and Web3 gaming backends. No node management is required.",
  },
  {
    q: "Can I build my own blockchain using Instanodes?",
    a: "Yes. Instanodes offers Rollup-as-a-Service (RaaS) on Arbitrum Orbit, OP Stack, Polygon CDK, and ZKsync. This covers deployment, bridge integration, block explorer setup, and ongoing infrastructure management. Custom appchain development is available for protocol teams and enterprises requiring dedicated engineering support from kickoff through production.",
    cta: { label: "Talk to our team", href: "/contact-us" },
  },
  {
    q: "How much does Node as a Service cost?",
    a: "Plans start free. Paid tiers begin at $29/month (60K requests/day) and scale to $169/month (1.6M requests/day). The Free tier includes 600K requests/month across 50+ blockchain networks with no credit card required. Enterprise teams with high-throughput or dedicated node requirements get custom plan pricing.",
    cta: { label: "View full pricing", href: "/pricing" },
  },
  {
    q: "Does Instanodes manage validator nodes?",
    a: "Yes. Instanodes runs proof-of-stake validator nodes across Ethereum, Cosmos, Polkadot, Avalanche, and other PoS networks. Teams who want to buy validator node hosting online get automated monitoring, upgrade management, slashing protection, and 24/7 incident response. Institutional clients get dedicated reporting dashboards, non-custodial key management guidance, and direct engineering access.",
    cta: { label: "Talk to our team", href: "/contact-us" },
  },
  {
    q: "How does Instanodes compare to Alchemy, Infura, or QuickNode?",
    a: "Alchemy, Infura, and QuickNode are built for individual developers on shared infrastructure. Instanodes is built for protocol teams and enterprises that need dedicated node capacity, institutional validator management, custom appchain deployment, and contractual SLAs. Teams that need lower latency variance, dedicated throughput, or sovereign chain control use Instanodes. The Free tier lets you test the difference before committing.",
    cta: { label: "Start for free", href: APP_LOGIN_URL },
  },
  {
    q: "Is Instanodes secure and compliant?",
    a: "Yes. Instanodes operates with encrypted API key management, role-based access controls, network-level DDoS mitigation, and isolated infrastructure per client. All plans include a contractual 99.95% uptime SLA backed by multi-region auto-failover and 24/7 monitoring. Enterprise clients get full security posture documentation and can discuss compliance requirements including data residency and audit access.",
    cta: { label: "Talk to our team", href: "/contact-us" },
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function HomepageFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section
      aria-labelledby="faq-heading"
      className="relative overflow-hidden border-t border-line py-[48px] sm:py-[64px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, color-mix(in oklab, var(--accent) 6%, transparent) 0%, transparent 60%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:px-8">
        {/* Left: heading column */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <h2
            id="faq-heading"
            className="font-accent text-[clamp(32px,4.2vw,48px)] font-normal leading-[1.05] tracking-[-0.025em] text-ink"
          >
            Questions, <em className="italic text-accent">answered.</em>
          </h2>
          <p className="mt-5 max-w-md text-[15px] leading-[1.6] text-ink-2">
            Everything teams ask before they ship. Need something specific?{" "}
            <a
              href="/contact-us"
              className="text-ink underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
            >
              Talk to an expert
            </a>
            .
          </p>
        </div>

        {/* Right: accordion */}
        <ul className="divide-y divide-line border-y border-line">
          {FAQS.map((item, i) => {
            const isOpen = openIdx === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <li key={item.q}>
                <h3 className="m-0">
                  <button
                    id={buttonId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIdx(isOpen ? null : i)}
                    className="group flex w-full items-start justify-between gap-6 py-6 text-left transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-4 focus-visible:ring-offset-canvas"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono text-[11px] tabular-nums tracking-[0.1em] text-ink-3">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[17px] font-medium leading-[1.4] tracking-[-0.005em] text-ink sm:text-[18px]">
                        {item.q}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink-2 transition-all duration-300 ease-[cubic-bezier(0.2,0,0,1)] group-hover:border-accent group-hover:text-accent ${
                        isOpen ? "rotate-45 border-accent text-accent" : ""
                      }`}
                    >
                      <Plus size={14} strokeWidth={1.75} />
                    </span>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.2,0,0,1)]"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="max-w-2xl overflow-hidden pb-7 pl-[44px] pr-10">
                    <p className="text-[15px] leading-[1.65] text-ink-2">
                      {item.a}
                    </p>
                    {item.cta && (
                      <a
                        href={item.cta.href}
                        className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
                      >
                        {item.cta.label} →
                      </a>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <JsonLd data={faqJsonLd} />
    </section>
  );
}
